import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { CustomIcon, Spinner } from '@components/shared';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { CloudinaryTransformations, CropperCanvasData } from '@root/types';
import { generateCloudinaryTransformations, generateZoomBounds } from './utils';

type ZoomBounds = { min: number; max: number; current: number; step: number };
export type CropBoxDimentions = { width: number; height: number };

type Props = {
  imageSrc: string;
  maxZoomAmount?: number;
  numberOfZoomSteps?: number;
  enableCropbox?: boolean;
  initialCanvasData?: CropperCanvasData | null;
  cropBoxDimentions?: CropBoxDimentions | null;
  cropBoxZoomAdjustment?: number;
  viewOnly?: boolean;
};
type UseCropperOutput = {
  isReady: boolean;
  ZoomRange: typeof ZoomRange;
  zoomRangeProps: ZoomRangeProps;
  Cropper: typeof CropperComponent;
  cropperProps: CropperComponentProps;
  isDragging: boolean;
  getCanvasData: () => CropperCanvasData | null;
  getCloudinaryTransformations: () => CloudinaryTransformations | null;
};

const useCropper = ({
  imageSrc,
  maxZoomAmount = 5,
  numberOfZoomSteps = 50,
  cropBoxDimentions,
  initialCanvasData,
  viewOnly = false,
}: Props): UseCropperOutput => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [zoomBounds, setZoomBounds] = useState<ZoomBounds>({
    min: 0,
    max: maxZoomAmount,
    current: 0,
    step: maxZoomAmount / numberOfZoomSteps,
  });

  const [isReady, setIsReady] = useState(false);
  useEffect(() => setIsReady(false), [imageSrc]);

  useEffect(() => {
    const cropper = cropperRef.current?.cropper;
    if (viewOnly) cropper?.disable();
    else cropper?.enable();
  }, [viewOnly, isReady]);

  const setCropBoxSize = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (cropper && cropBoxDimentions && isReady) {
      const { height, width } = cropper.getContainerData();
      cropper.setCropBoxData({
        ...cropBoxDimentions,
        top: height / 2 - cropBoxDimentions.height / 2,
        left: width / 2 - cropBoxDimentions.width / 2,
      });
    }
  }, [cropBoxDimentions, isReady]);

  useEffect(() => {
    setCropBoxSize();
  }, [cropBoxDimentions, isReady, setCropBoxSize]);

  useEffect(() => {
    const cropper = cropperRef.current?.cropper;
    if (cropper && initialCanvasData && isReady) {
      cropper.setCanvasData(initialCanvasData);
    }
  }, [initialCanvasData, setCropBoxSize, isReady]);

  const zoomTo = useCallback(
    (zoomAmount: number) => {
      const cropper = cropperRef.current?.cropper;
      if (cropper && isReady && zoomAmount) {
        cropper.zoomTo(zoomAmount, {
          x: cropper.getContainerData().width / 2,
          y: cropper.getContainerData().height / 2,
        });

        // For some reason changing the zoom can affect the cropbox size
        setCropBoxSize();
      }
    },
    [isReady, setCropBoxSize],
  );

  useEffect(() => {
    const cropper = cropperRef.current?.cropper;
    const minImageDimentions = cropBoxDimentions || cropper?.getContainerData();
    const canvasData = cropper?.getCanvasData();
    if (cropper && isReady && minImageDimentions?.width && canvasData?.width) {
      const newZoomBounds = generateZoomBounds({
        minImageDimentions,
        canvasData,
        maxZoomAmount,
        numberOfZoomSteps,
        useCanvasDimentions: !!initialCanvasData,
      });
      setZoomBounds(newZoomBounds);
      zoomTo(newZoomBounds.current);

      const handleZoom = e => {
        let currentZoom = e.detail.ratio;
        if (currentZoom > newZoomBounds.max) {
          e.preventDefault();
          currentZoom = newZoomBounds.max;
          zoomTo(newZoomBounds.max);
        } else if (currentZoom < newZoomBounds.min) {
          e.preventDefault();
          currentZoom = newZoomBounds.min;
          zoomTo(newZoomBounds.min);
        }
        setZoomBounds(prevZoomBounds => ({ ...prevZoomBounds, current: currentZoom }));
      };

      const cropperRefCurrent = cropperRef.current!;
      cropperRefCurrent.addEventListener('zoom', handleZoom);
      return () => cropperRefCurrent.removeEventListener('zoom', handleZoom);
    }
    return () => {};
  }, [
    isReady,
    maxZoomAmount,
    imageSrc,
    numberOfZoomSteps,
    zoomTo,
    cropBoxDimentions,
    initialCanvasData,
  ]);

  const handleKeyDown = event => {
    if (event.key === '-') zoomTo(zoomBounds.current - zoomBounds.step);
    if (event.key === '=') zoomTo(zoomBounds.current + zoomBounds.step);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  });

  const cropper = cropperRef.current?.cropper;
  return {
    isReady,
    ZoomRange,
    zoomRangeProps: {
      zoomBounds,
      isReady,
      zoomTo,
    },
    Cropper: CropperComponent,
    cropperProps: {
      isDragging,
      setIsDragging,
      isReady,
      onReady: () => setIsReady(true),
      imageSrc,
      cropperRef,
      viewOnly,
      enableCropbox: !!cropBoxDimentions,
    },
    isDragging,
    getCanvasData: () => cropper?.getCanvasData() || null,
    getCloudinaryTransformations: () => {
      if (!cropper) return null;

      const cropBox = cropBoxDimentions
        ? cropper.getCropBoxData()
        : { left: 0, top: 0, ...cropper.getContainerData() };

      return generateCloudinaryTransformations({
        cropBox,
        canvasData: cropper.getCanvasData(),
        zoom: zoomBounds.current,
      });
    },
  };
};

type ZoomRangeProps = {
  zoomBounds: ZoomBounds;
  className?: string;
  isReady: boolean;
  zoomTo: (number) => void;
  inputClassName?: string;
};
const ZoomRange = ({ zoomBounds, className, isReady, zoomTo, inputClassName }: ZoomRangeProps) => (
  <div
    className={cn(
      className,
      'pointer-events-auto flex items-center gap-1 rounded-[50px] bg-black-100/60 p-1 text-white font-body-medium',
      isReady ? 'opacity-100' : 'opacity-0',
    )}
  >
    <CustomIcon type="minus" />
    <input
      data-testid="cropperZoom"
      type="range"
      min={zoomBounds.min}
      max={zoomBounds.max}
      step={zoomBounds.step}
      className={cn(
        'h-0.5 w-20 cursor-pointer appearance-none rounded-none accent-white [&::-webkit-slider-thumb]:rounded-[50px] [&::-webkit-slider-thumb]:shadow-[0_0_0_3px_rgba(85,_40,_255,_1)]',
        inputClassName,
      )}
      value={zoomBounds.current}
      onChange={e => {
        zoomTo(e.target.valueAsNumber);
      }}
    />
    <CustomIcon type="plus" />
  </div>
);

type CropperComponentProps = {
  containerClassName?: string;
  className?: string;
  dragTextClassName?: string;
  isDragging: boolean;
  setIsDragging: (boolean) => void;
  viewOnly: boolean;
  isReady: boolean;
  enableCropbox: boolean;
  onReady: () => void;
  imageSrc: string;
  cropperRef: React.ForwardedRef<ReactCropperElement>;
  alt?: string;
};
const CropperComponent = ({
  containerClassName,
  className,
  dragTextClassName,
  isDragging,
  setIsDragging,
  enableCropbox,
  viewOnly,
  isReady,
  onReady,
  imageSrc,
  cropperRef,
  alt,
}: CropperComponentProps) => {
  const { t } = useTranslation('common');

  return (
    <div className={containerClassName}>
      {!isReady && <Spinner className="absolute inset-[50%] ml-[-8px] mt-[-24px] text-[32px]" />}
      <Cropper
        key={imageSrc}
        className={cn(
          className,
          // eslint-disable-next-line no-nested-ternary
          viewOnly
            ? '[&_.cropper-drag-box]:!cursor-auto [&_.cropper-face]:!cursor-auto'
            : isDragging
            ? '[&_.cropper-drag-box]:!cursor-grabbing [&_.cropper-face]:!cursor-grabbing'
            : '[&_.cropper-drag-box]:!cursor-grab [&_.cropper-face]:!cursor-grab',
        )}
        src={imageSrc}
        alt={alt}
        dragMode="move"
        cropBoxMovable={false}
        cropBoxResizable={false}
        viewMode={enableCropbox ? 1 : 3}
        autoCropArea={1}
        guides={false}
        ref={cropperRef}
        responsive={false}
        restore={false}
        autoCrop={enableCropbox}
        toggleDragModeOnDblclick={false}
        ready={onReady}
        cropmove={() => setIsDragging(true)}
        cropend={() => setIsDragging(false)}
        background={false}
      />
      <div
        className={cn(
          'select-none transition',
          isDragging || viewOnly || !isReady ? 'opacity-0' : 'opacity-100',
        )}
      >
        <div className="pointer-events-none absolute top-2 flex w-full justify-center">
          <div
            className={cn(
              'flex items-center gap-1 rounded-[3px] bg-black-100/60 p-1 text-white font-body-medium',
              dragTextClassName,
            )}
          >
            <CustomIcon type="move" />
            {t('cropper.dragImageToReposition')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default useCropper;
